// Thumbnail resolution for models. Extracted verbatim from main.js so it can be unit tested;
// main.js keeps `modelColorName` / `modelThumbnailDetails` as thin delegating root methods.
// src/shared/composables/modelThumbnail.ts in the Vue3 repo is a deliberate port of this file.

// Frozen because main.js hands this exact array to root state as `modelExtensions`; every
// reference across the app is a read (`.includes`), and freezing keeps it that way.
export const MODEL_EXTENSIONS = Object.freeze(['stl', 'obj', 'step', 'stp', 'sldprt', 'asm', 'f3d', 'fbx', 'iam', 'ipt', 'neu', 'prt', 'sldasm', 'smb', 'smt', 'stpz', 'wire', 'x_b', 'x_t', '3mf'])

// Returns a hash of the models color, including color changes
export function modelColorName (model, modelData) {
  if (!model || !modelData || !model.requestedSettings) return ''
  return [
      model.requestedSettings.Color,
      ...(model.colorChanges || []).filter(x => x.color).map(cc => (cc.height.replace('.', '-') || '') + cc.color)
  ].join('_')
}

export function modelThumbnailDetails (modelDataOrProject = {}, model, modelExtensions = MODEL_EXTENSIONS) {
  let modelData = modelDataOrProject || {}
  if (modelDataOrProject.modelData) modelData = modelDataOrProject.modelData[model.uuid] || {}

  const colorThumbnails = modelData.colorThumbnails || {}
  const colorKey = modelColorName(model, modelData)
  const v3Thumbnail = colorThumbnails[colorKey]
  const v3DefaultThumbnail = colorThumbnails.Default || ''
  if (v3Thumbnail) return { loading: false, url: v3Thumbnail }

  // Nothing rendered for this colour. Best available stand-in, in order: the neutral Default
  // render, then the pre-colour v2/v1 renders. This used to short-circuit on
  // `Object.keys(colorThumbnails).length` and hand back `v3DefaultThumbnail || ''`, so any
  // colorThumbnails map that didn't resolve for the current colour pinned the model to
  // { loading: true, url: '' } — an indefinite spinner over a perfectly good thumbnail, with
  // nothing to ever reconcile it. `url` must never come back empty while some render exists.
  const v2Thumbnail = (modelData.thumbnails || [])[0] || ''
  const v1Thumbnail = (model.thumbnails || [])[0] || ''
  const standInThumbnail = v3DefaultThumbnail || v2Thumbnail || v1Thumbnail

  // `loading` reports a fact about what we just handed back — "this is the neutral Default
  // render standing in for a colour render" — rather than a claim about a write we can't
  // verify. Deliberately NOT keyed off an empty-string sentinel at the current colour key:
  // nothing reconciles a stranded sentinel (the card that wrote it may be long gone, and its
  // cleanup write is best-effort), and the `!loading && url` gates in ModelVisualizationPane
  // — the units select, the units warning, the size menu — are not behind isUploadCompleted,
  // so a sentinel that outlived its render would hide them permanently. Nothing is lost on the
  // legitimate re-render path: ModelVisualizer's own spinner keys off an empty `url`, not off
  // this flag, and every consumer that shows "Generating thumbnail..." also requires
  // isUploadCompleted.
  if (standInThumbnail) return { loading: standInThumbnail === v3DefaultThumbnail, url: standInThumbnail }

  const modelJobThumbnail = modelData?.jobAnalysis?.thumbnail || ''
  if (modelJobThumbnail) return { loading: true, url: modelJobThumbnail }

  // Fallback: show content or a file-type icon for non-3D formats
  try {
    const nameFromModel = (model && model.name) ? (model.name + '') : ''
    const nameFromOriginal = (modelData && modelData.originalFile && modelData.originalFile.name) ? (modelData.originalFile.name + '') : ''
    const filename = nameFromModel || nameFromOriginal || ''
    const ext = (filename.toLowerCase().split('.').pop() || '').trim()
    const non3dExts = ['dxf', 'ai', 'cdr', 'pdf', 'eps', 'svg']
    if (ext && non3dExts.includes(ext)) {
      // Map specific icons when available; otherwise use a generic file icon
      const iconMap = {
        dxf: require('@/assets/filetypes/file-dxf.svg'),
        ai: require('@/assets/filetypes/file-ai.svg'),
        cdr: require('@/assets/filetypes/file-cdr.svg'),
        pdf: require('@/assets/filetypes/file-pdf.svg'),
        eps: require('@/assets/filetypes/file-eps.svg'),
        svg: require('@/assets/filetypes/file-svg.svg'),
      }
      const iconUrl = iconMap[ext] || require('@/assets/filetypes/file-generic.svg')
      return { loading: false, url: iconUrl }
    }
    // Unknown filetypes: prefer a generic icon over a perpetual spinner
    if (!ext || !ext.length) {
      const genericIcon = require('@/assets/filetypes/file-generic.svg')
      return { loading: false, url: genericIcon }
    }
  } catch (e) {}

  // Default: if this is a mesh extension, keep showing generating state; otherwise use generic icon
  try {
    const name = (model && model.name) ? (model.name + '') : ((modelData && modelData.originalFile && modelData.originalFile.name) ? (modelData.originalFile.name + '') : '')
    const ext = (name.toLowerCase().split('.').pop() || '').trim()
    const isMesh = Array.isArray(modelExtensions) && modelExtensions.includes(ext)
    if (isMesh) {
      return { loading: true, url: '' }
    }
    const genericIcon = require('@/assets/filetypes/file-generic.svg')
    return { loading: false, url: genericIcon }
  } catch (e) {
    return { loading: true, url: '' }
  }
}
